import React, { useMemo, useRef, useState } from 'react';
import { format } from 'date-fns';
import { Loader2, Upload } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Input } from '@/components/ui/input';
import { supabase } from '@/integrations/supabase/client';
import { DEFAULT_IDENTIFIER_LABELS } from '@/shared/identifierResolution/standardIdentifierTypes';
import {
  IDENTIFIER_CONFIG_INVALID_INPUT,
  IDENTIFIER_CONFIG_NO_USABLE_HEADERS,
} from '@/shared/types/organisationCustomIdentifier';
import {
  useOrganisationCustomIdentifiers,
  type CustomIdentifierRow,
} from '@/hooks/useOrganisationCustomIdentifiers';

const fromAny = (table: string) =>
  (supabase as unknown as { from: (t: string) => ReturnType<typeof supabase.from> }).from(table);

const IGNORE = '__ignore__';
const CREATE_NEW = '__create_new__';

type HeaderAction = typeof IGNORE | typeof CREATE_NEW | string;

function formatAddedAt(createdAt: string | undefined): string {
  if (!createdAt) return '—';
  const parsed = new Date(createdAt);
  if (Number.isNaN(parsed.getTime())) return '—';
  return format(parsed, 'MMM d, yyyy');
}

function splitCsvLine(line: string): string[] {
  const values: string[] = [];
  let current = '';
  let inQuotes = false;
  for (const char of line) {
    if (char === '"') inQuotes = !inQuotes;
    else if (char === ',' && !inQuotes) {
      values.push(current.trim().replace(/^"|"$/g, ''));
      current = '';
    } else current += char;
  }
  values.push(current.trim().replace(/^"|"$/g, ''));
  return values.filter((h) => h.length > 0);
}

interface Props {
  organisationId: string;
}

export function CustomCsvIdentifierSection({ organisationId }: Props) {
  const fileRef = useRef<HTMLInputElement>(null);
  const { toast } = useToast();
  const existingQuery = useOrganisationCustomIdentifiers(organisationId);

  const [parsing, setParsing] = useState(false);
  const [headers, setHeaders] = useState<string[]>([]);
  const [actions, setActions] = useState<HeaderAction[]>([]);
  const [saving, setSaving] = useState(false);
  const [lastErrorType, setLastErrorType] = useState<string | null>(null);

  const existingNames = useMemo(
    () => (existingQuery.data ?? []).map((row) => row.name),
    [existingQuery.data],
  );

  const mapTargets = useMemo(() => {
    const standards = [...DEFAULT_IDENTIFIER_LABELS];
    const customs = existingNames.filter(
      (n) => !standards.some((s) => s.toLowerCase() === n.toLowerCase()),
    );
    return { standards, customs };
  }, [existingNames]);

  const handleFile = async (file: File | undefined) => {
    if (!file) return;
    setParsing(true);
    setLastErrorType(null);
    try {
      const text = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(String(reader.result ?? ''));
        reader.onerror = () => reject(reader.error ?? new Error('Failed to read CSV'));
        reader.readAsText(file);
      });
      const firstLine = text.split(/\r?\n/).find((l) => l.trim());
      const detected = firstLine ? splitCsvLine(firstLine) : [];
      if (detected.length === 0) {
        setHeaders([]);
        setActions([]);
        setLastErrorType(IDENTIFIER_CONFIG_NO_USABLE_HEADERS);
        toast({
          title: 'No usable headers',
          description: 'This CSV has no usable column headers to map.',
          variant: 'destructive',
        });
        return;
      }
      setHeaders(detected);
      setActions(detected.map(() => IGNORE));
    } finally {
      setParsing(false);
    }
  };

  const setAction = (index: number, value: HeaderAction) => {
    setActions((prev) => {
      const next = [...prev];
      next[index] = value;
      return next;
    });
  };

  const persistName = async (
    name: string,
    mapsTo: string | null,
  ): Promise<{ ok: boolean; type?: string; row?: CustomIdentifierRow }> => {
    const trimmed = name.trim();
    if (!trimmed) {
      return { ok: false, type: IDENTIFIER_CONFIG_INVALID_INPUT };
    }
    const { data, error } = await fromAny('organisation_custom_identifiers')
      .insert({
        organisation_id: organisationId,
        name: trimmed,
        maps_to: mapsTo,
      })
      .select('id, organisation_id, name, maps_to, created_at')
      .single();
    if (error) {
      return { ok: false, type: IDENTIFIER_CONFIG_INVALID_INPUT };
    }
    return { ok: true, row: data as CustomIdentifierRow };
  };

  const handleSave = async () => {
    const usable = headers.filter((_, i) => actions[i] && actions[i] !== IGNORE);
    if (headers.length === 0) {
      setLastErrorType(IDENTIFIER_CONFIG_NO_USABLE_HEADERS);
      toast({
        title: 'No usable headers',
        description: 'This CSV has no usable column headers to map.',
        variant: 'destructive',
      });
      return;
    }
    if (usable.length === 0) {
      toast({
        title: 'Nothing to save',
        description: 'No columns were mapped. Existing default identifiers still work.',
      });
      return;
    }

    setSaving(true);
    setLastErrorType(null);
    let created = 0;
    try {
      for (let i = 0; i < headers.length; i++) {
        const action = actions[i];
        if (!action || action === IGNORE) continue;
        const header = headers[i];
        const mapsTo = action === CREATE_NEW ? null : action;
        const result = await persistName(header, mapsTo);
        if (!result.ok) {
          setLastErrorType(result.type ?? IDENTIFIER_CONFIG_INVALID_INPUT);
          toast({
            title: 'Invalid identifier name',
            description: `Could not save “${header}” as a custom identifier (duplicate, blank, or invalid name).`,
            variant: 'destructive',
          });
          return;
        }
        created += 1;
      }
      await existingQuery.refetch();
      toast({
        title: 'Saved',
        description:
          created === 1
            ? 'Saved 1 custom CSV identifier name.'
            : `Saved ${created} custom CSV identifier names.`,
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className="space-y-4" data-testid="custom-csv-identifier-section">
      <div>
        <h2 className="text-lg font-semibold">Custom CSV Identifier</h2>
        <p className="text-sm text-muted-foreground">
          Upload a sample CSV, map detected headers to an existing identifier, or save them as
          new custom identifier names for Inventory Snapshot and adjustment uploads.
        </p>
      </div>

      <div className="space-y-2" data-testid="configured-custom-identifiers">
        <h3 className="text-sm font-medium">
          Configured custom identifiers ({existingQuery.data?.length ?? 0})
        </h3>
        {existingQuery.isLoading ? (
          <p className="text-muted-foreground text-sm">Loading…</p>
        ) : !existingQuery.data || existingQuery.data.length === 0 ? (
          <p className="text-muted-foreground text-sm text-center py-6">
            No custom identifiers configured yet. Upload a sample CSV below to add them.
          </p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>CSV header</TableHead>
                <TableHead>Treats as</TableHead>
                <TableHead>Added</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {existingQuery.data.map((row) => (
                <TableRow key={row.id}>
                  <TableCell className="font-medium">{row.name}</TableCell>
                  <TableCell>{row.maps_to ?? 'Custom identifier'}</TableCell>
                  <TableCell>{formatAddedAt(row.created_at)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>

      <div className="space-y-2">
        <Label htmlFor="custom-csv-identifier-file">Sample CSV</Label>
        <div
          className="border-2 border-dashed rounded-lg p-6 text-center cursor-pointer hover:border-primary/50 transition-colors"
          onClick={() => fileRef.current?.click()}
        >
          {parsing ? (
            <Loader2 className="h-6 w-6 mx-auto animate-spin text-muted-foreground" />
          ) : (
            <Upload className="h-6 w-6 mx-auto mb-2 text-muted-foreground" />
          )}
          <p className="text-sm text-muted-foreground">
            {headers.length === 0
              ? 'Choose a CSV to detect column headers'
              : `${headers.length} columns detected`}
          </p>
        </div>
        <Input
          id="custom-csv-identifier-file"
          ref={fileRef}
          type="file"
          accept=".csv,text/csv"
          className="hidden"
          onChange={(e) => void handleFile(e.target.files?.[0])}
        />
      </div>

      {lastErrorType === IDENTIFIER_CONFIG_NO_USABLE_HEADERS && (
        <p className="text-sm text-destructive" role="alert">
          No usable headers. {IDENTIFIER_CONFIG_NO_USABLE_HEADERS}
        </p>
      )}
      {lastErrorType === IDENTIFIER_CONFIG_INVALID_INPUT && (
        <p className="text-sm text-destructive" role="alert">
          Invalid identifier name. {IDENTIFIER_CONFIG_INVALID_INPUT}
        </p>
      )}

      {headers.length > 0 && (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Detected header</TableHead>
              <TableHead>Map or create</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {headers.map((header, i) => (
              <TableRow key={`${header}-${i}`}>
                <TableCell className="font-medium">{header}</TableCell>
                <TableCell>
                  <Select value={actions[i] ?? IGNORE} onValueChange={(v) => setAction(i, v)}>
                    <SelectTrigger aria-label={`Map ${header}`}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value={IGNORE}>Ignore</SelectItem>
                      <SelectItem value={CREATE_NEW}>Create as new custom identifier</SelectItem>
                      {mapTargets.standards.map((name) => (
                        <SelectItem key={`std-${name}`} value={name}>
                          Map to {name}
                        </SelectItem>
                      ))}
                      {mapTargets.customs.map((name) => (
                        <SelectItem key={`custom-${name}`} value={name}>
                          Map to {name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      <Button
        type="button"
        onClick={() => void handleSave()}
        disabled={saving || parsing}
      >
        {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
        Save identifier names
      </Button>
    </section>
  );
}
